import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { TOOL_TYPES, TOOL_CATEGORIES, getToolsByCategory, getToolCategory } from '../../store/useToolStore';

const TOOL_ROUTES = {
  [TOOL_TYPES.LOADER]: { path: '/tools/loader', icon: '⚙️', name: 'Loaders' },
  [TOOL_TYPES.GRADIENT]: { path: '/tools/gradient', icon: '🌈', name: 'Gradients' },
  [TOOL_TYPES.CARD]: { path: '/tools/card', icon: '🪪', name: 'Cards' },
  [TOOL_TYPES.ANIMATION]: { path: '/tools/animation', icon: '⚡', name: 'Animations' },
  [TOOL_TYPES.ALERT]: { path: '/tools/alert', icon: '🔔', name: 'Alerts' },
  [TOOL_TYPES.PHOTO_PICKER]: { path: '/tools/photo-picker', icon: '📸', name: 'Photo Picker' },
  [TOOL_TYPES.JSON_TO_MODEL]: { path: '/tools/json-to-model', icon: '🔍', name: 'JSON → Model' },
  [TOOL_TYPES.API_GENERATOR]: { path: '/tools/api-generator', icon: '🌐', name: 'API Generator' },
  [TOOL_TYPES.STORYBOARD_CONVERTER]: { path: '/tools/storyboard-converter', icon: '🧰', name: 'Storyboard → SwiftUI' },
  [TOOL_TYPES.POSTMAN_IMPORTER]: { path: '/tools/postman-importer', icon: '📝', name: 'Postman Import' },
};

const Toolbar = () => {
  const location = useLocation();
  const [showMore, setShowMore] = useState(false);
  const [activeCategory, setActiveCategory] = useState(() => {
    const toolType = Object.entries(TOOL_ROUTES).find(([_, tool]) => tool.path === location.pathname)?.[0];
    return toolType ? getToolCategory(toolType) : TOOL_CATEGORIES.SWIFTUI;
  });

  const isActive = (path) => location.pathname === path;
  
  // Update active category when route changes
  useEffect(() => {
    const toolType = Object.entries(TOOL_ROUTES).find(([_, tool]) => tool.path === location.pathname)?.[0];
    if (toolType) {
      setActiveCategory(getToolCategory(toolType));
    }
  }, [location.pathname]);

  return (
    <div className="bg-gray-50 dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 transition-colors duration-200">
      <div className="max-w-[1400px] mx-auto px-2 sm:px-4 py-2 sm:py-3">
        {/* Desktop: Category Tabs + Horizontal Scroll */}
        <div className="hidden md:block">
        {/* Category Tabs */}
        <div className="flex items-center gap-2 mb-2">
          <button
            onClick={() => setActiveCategory(TOOL_CATEGORIES.SWIFTUI)}
            className={`px-3 py-1 rounded text-xs font-semibold transition-colors ${
              activeCategory === TOOL_CATEGORIES.SWIFTUI
                ? 'bg-gradient-primary text-white'
                : 'bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-400 hover:bg-gray-300 dark:hover:bg-gray-600'
            }`}
          >
            SwiftUI (UI)
          </button>
          <button
            onClick={() => setActiveCategory(TOOL_CATEGORIES.SWIFT)}
            className={`px-3 py-1 rounded text-xs font-semibold transition-colors ${
              activeCategory === TOOL_CATEGORIES.SWIFT
                ? 'bg-gradient-primary text-white'
                : 'bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-400 hover:bg-gray-300 dark:hover:bg-gray-600'
            }`}
          >
            Swift (Functional)
          </button>
        </div>
        {/* Tools Scroll */}
        <div className="flex items-center gap-2 overflow-x-auto scrollbar-hide">
          {getToolsByCategory(activeCategory)
            .filter((type) => type !== TOOL_TYPES.STORYBOARD_CONVERTER)
            .map((type) => {
            const tool = TOOL_ROUTES[type];
            return (
              <Link
                key={type}
                to={tool.path}
                className={`
                  flex items-center gap-2 px-3 sm:px-4 py-2 rounded-lg transition-all whitespace-nowrap text-sm
                  ${
                    isActive(tool.path)
                      ? 'bg-primary-600 text-white shadow-lg'
                      : 'bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-300 dark:hover:bg-gray-600'
                  }
                `}
              >
                <span className="text-base sm:text-lg">{tool.icon}</span>
                <span className="font-medium hidden sm:inline">{tool.name}</span>
              </Link>
            );
          })}
        </div>
        </div>

        {/* Mobile: Category Tabs + Dropdown */}
        <div className="md:hidden">
          {/* Category Tabs */}
          <div className="flex items-center gap-2 mb-2">
            <button
              onClick={() => setActiveCategory(TOOL_CATEGORIES.SWIFTUI)}
              className={`flex-1 px-3 py-1 rounded text-xs font-semibold transition-colors ${
                activeCategory === TOOL_CATEGORIES.SWIFTUI
                  ? 'bg-gradient-primary text-white'
                  : 'bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-400'
              }`}
            >
              SwiftUI
            </button>
            <button
              onClick={() => setActiveCategory(TOOL_CATEGORIES.SWIFT)}
              className={`flex-1 px-3 py-1 rounded text-xs font-semibold transition-colors ${
                activeCategory === TOOL_CATEGORIES.SWIFT
                  ? 'bg-gradient-primary text-white'
                  : 'bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-400'
              }`}
            >
              Swift
            </button>
          </div>
          <button
            onClick={() => setShowMore(!showMore)}
            className="w-full flex items-center justify-between px-4 py-2 bg-gray-200 dark:bg-gray-700 rounded-lg text-gray-700 dark:text-gray-300 hover:bg-gray-300 dark:hover:bg-gray-600 transition-colors"
          >
            <span className="font-medium">Select Tool</span>
            <svg className={`w-5 h-5 transition-transform ${showMore ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </button>
          {showMore && (
            <div className="mt-2 space-y-1">
              {getToolsByCategory(activeCategory)
                .filter((type) => type !== TOOL_TYPES.STORYBOARD_CONVERTER)
                .map((type) => {
                const tool = TOOL_ROUTES[type];
                return (
                  <Link
                    key={type}
                    to={tool.path}
                    onClick={() => setShowMore(false)}
                    className={`
                      flex items-center gap-3 px-4 py-2 rounded-lg transition-colors
                      ${
                        isActive(tool.path)
                          ? 'bg-gradient-primary text-white'
                          : 'bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-300 dark:hover:bg-gray-600'
                      }
                    `}
                  >
                    <span className="text-xl">{tool.icon}</span>
                    <span className="font-medium">{tool.name}</span>
                  </Link>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Toolbar;

